import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { ReactCheckboxTristate } from '~src/commonInfra/components/checkbox/ReactCheckboxTristate';
import { SiteRolePickerMenu } from '~src/userManagement/components/siteRolePicker/SiteRolePickerMenu';
import { ADMIN_PROFILE_LIST_MAX_HEIGHT } from '~src/userManagement/helpers/AdminProfileDropdownHelper';
import {
  SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_CONTROL_WIDTH,
  SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_LEFT_INSET,
  SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
  SITE_MEMBERSHIP_GRID_DROPDOWN_WIDTH,
} from '~src/userManagement/helpers/SiteMembershipGridAdminProfileHelper';

import { Messages } from '../../codegen/Localize';
import { ISiteNameWithId } from '../serverApi/types/ISiteNameWithId';

export interface ISiteGridAdminProfileCellProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  listItems: ListItem[];
}

const HEADER_PADDING = '0 2px';
const SITE_CELL_LEFT_PADDING = 2;

function SiteGridHeader(props: { col: { headerText: string }, align?: 'left' | 'center' }) {
  const textAlign = props.align === 'left' ? 'left' : 'center';
  return (
    <span style={{ display: 'block', padding: HEADER_PADDING, textAlign, width: '100%' }}>
      {props.col.headerText}
    </span>
  );
}

function SiteGridSiteNameCell(props: BodyCell.Props): React.ReactElement {
  return (
    <span style={{ paddingLeft: SITE_CELL_LEFT_PADDING, display: 'block', textAlign: 'left' }}>
      {props.value}
    </span>
  );
}

export module SiteMembershipGridColumns {

  function SiteGridCheckboxCell(checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props): React.FC<BodyCell.Props> {
    return function SiteGridCheckboxCell(props: BodyCell.Props) {
      const row = props.row;
      const checkboxClass = 'tb-checkbox-site-grid-cell';
      return (
        <span className={checkboxClass}>
          <ReactCheckboxTristate {...checkboxProps(row.data as ISiteNameWithId)} />
        </span>
      );
    };
  }

  function SiteGridDropdownCell(dropdownProps: (site: ISiteNameWithId) => SiteRolePickerMenu.Props, checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props): React.FC<BodyCell.Props> {
    return function SiteGridDropdownCell(props: BodyCell.Props) {
      const data = props.row.data as ISiteNameWithId;
      if (checkboxProps(data) && (checkboxProps(data).checkboxState !== 'empty')) {
        return (
          <span style={{ marginRight: 8 }}>
            <SiteRolePickerMenu {...dropdownProps(data)} tabIndex={-1} siteName={data.name} />
          </span>
        );
      } else {
        return null;
      }
    };
  }

  function SiteGridAdminProfileCell(
    adminProfileProps: (site: ISiteNameWithId) => ISiteGridAdminProfileCellProps | null,
    checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props
  ): React.FC<BodyCell.Props> {
    return function SiteGridAdminProfileCellInner(props: BodyCell.Props) {
      const data = props.row.data as ISiteNameWithId;
      const checkboxState = checkboxProps(data)?.checkboxState;
      if (checkboxState === 'empty') {
        return null;
      }
      const apProps = adminProfileProps(data);
      if (!apProps) {
        return null;
      }
      // Popup width follows the List. Use POPUP_LIST_WIDTH (not CONTROL_WIDTH): the widgets FocusTrap adds a 1px border
      // on each side while minWidth matches the button; a List as wide as the button forces the wrapper ~2px wider.
      const listStyle: React.CSSProperties = {
        maxHeight: ADMIN_PROFILE_LIST_MAX_HEIGHT,
        overflow: 'auto',
        width: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
        minWidth: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
        maxWidth: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_POPUP_LIST_WIDTH,
      };
      return (
        <div style={{ display: 'inline-block', marginLeft: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_LEFT_INSET }}>
          <SingleSelectDropdown
            label=""
            value={apProps.value}
            onChange={apProps.onChange}
            disabled={apProps.disabled}
            list={<List items={apProps.listItems} style={listStyle} />}
            type={DropdownButtonType.Outline}
            style={{ width: SITE_MEMBERSHIP_GRID_ADMIN_PROFILE_CONTROL_WIDTH }}
            tabIndex={-1}
            testId="site-membership-admin-profile-picker"
          />
        </div>
      );
    };
  }

  export function columns(
    dropdownProps: (site: ISiteNameWithId) => SiteRolePickerMenu.Props,
    checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props,
    adminProfileProps?: (site: ISiteNameWithId) => ISiteGridAdminProfileCellProps | null
  ): DataGrid.Column[] {

    const columns: DataGrid.Column[] = [
      {
        name: 'select-site-col',
        dataKey: '0',
        headerText: Messages.column_select(),
        headerComponent: props => <SiteGridHeader col={props.col} />,
        cellComponent: SiteGridCheckboxCell(checkboxProps),
        preferredWidth: 52,
        resizable: false,
        sortable: false,
      },
      {
        name: 'site-name-col',
        dataKey: 'name',
        headerText: Messages.site(),
        headerComponent: props => <SiteGridHeader col={props.col} align="left" />,
        cellComponent: SiteGridSiteNameCell,
        preferredWidth: 180,
        resizable: false,
        sortable: false
      },
      {
        name: 'site-select-col',
        dataKey: '2',
        headerText: Messages.siteRole(),
        headerComponent: props => <SiteGridHeader col={props.col} />,
        cellComponent: SiteGridDropdownCell(dropdownProps, checkboxProps),
        preferredWidth: SITE_MEMBERSHIP_GRID_DROPDOWN_WIDTH,
        resizable: false,
        sortable: false
      }
    ];

    if (adminProfileProps) {
      columns.push({
        name: 'admin-profile-col',
        dataKey: '3',
        headerText: Messages.column_adminProfile(),
        headerComponent: props => <SiteGridHeader col={props.col} />,
        cellComponent: SiteGridAdminProfileCell(adminProfileProps, checkboxProps),
        preferredWidth: SITE_MEMBERSHIP_GRID_DROPDOWN_WIDTH,
        resizable: false,
        sortable: false
      });
    }

    return columns;
  }
}
